import baseAxios from 'axios';
import {axios} from '@http_client';
import {$http} from "@http";
import md5 from "md5";
import {checkUrlParams, linkTypes} from "@models/check_link";
import {httpBuildQuery, isEmpty, str_replace} from "@helpers/helpers";
import {getDefaultCity} from "@hooks/cities";
import {getDealTypeValues, getUrlDealTypes, getUrlTypes, getUrlTypesByCityId, getVillaTypes} from "./property";
import {array_intersect, array_unique, inArray, is_array, ObjectLength} from "@helpers/arrays";
import {getCity, getCityURL} from "./district";
import {getDistrict} from "@models/district";
import {shortAreaNumber, shortMoneyNumber} from "@helpers/numbers";
import {getDefaultPrices} from "../components/hooks/prices";
import {getDefaultInfrastructureArea} from "../components/hooks/infrastructure_area";
import {checkRedirectToNorthCityLanding} from "../components/organisms/property/landing/north_cities";
import {getCacheKey} from "./cache_key";


const cacheTable = 'search';

export function mergeFiltersParams(filterUrlParams) {
    const items = ['district_id[]', 'types[]', 'floors[]', 'bedrooms[]', 'densities[]', 'deeds[]', 'facilities[]', 'villa_types[]'];

    for (let item of items) {
        const key = str_replace('[]', '', item);

        if (!isEmpty(filterUrlParams) && !isEmpty(filterUrlParams[item])) {
            if (is_array(filterUrlParams[item]) && !isEmpty(filterUrlParams[key])) {
                filterUrlParams[key] = [...filterUrlParams[key], ...filterUrlParams[item]];
            } else if (!isEmpty(filterUrlParams[item]) && !isEmpty(filterUrlParams[key])) {
                filterUrlParams[key] = [...filterUrlParams[key], ...[filterUrlParams[item]]];
            } else {
                filterUrlParams[key] = (is_array(filterUrlParams[item]) ? filterUrlParams[item] : [filterUrlParams[item]]);
            }

            filterUrlParams[key] = array_unique(filterUrlParams[key]);
            delete filterUrlParams[item];
        } else if (!isEmpty(filterUrlParams) && !isEmpty(filterUrlParams[key])) {
            if (!is_array(filterUrlParams[key])) {
                filterUrlParams[key] = [filterUrlParams[key]]
            }
        }
    }

    if (!isEmpty(filterUrlParams) && !isEmpty(filterUrlParams['deal_type']) && is_array(filterUrlParams['deal_type'])) {
        filterUrlParams['deal_type'] = filterUrlParams['deal_type'][0];
    }

    return filterUrlParams;
}


const CancelToken = baseAxios.CancelToken;
let cancelProperties;

async function getProperties(page, last_route, filters, doCancel = true, count = 20) {
    let route = '/search/v1/properties?page=' + page + '&';
    let properties = {properties: [], total: 0};

    route += httpBuildQuery(filters);

    if (count !== 20) {
        route += '&count=' + count;
    }

    if (route !== last_route) {
        // Cancel request
        if (!isEmpty(cancelProperties) && doCancel) {
            cancelProperties();
        }

        const cacheKey = getCacheKey(cacheTable, 'properties', 'hash', [md5(route)]);
        const cacheTTL = 5 * 60; // second

        await $http(cacheKey, route, cacheTTL, 'get', null, true).then(result => {
            if (!isEmpty(result)) {
                properties = result;
                properties['last_route'] = route;
                properties['loading'] = false;
            }
        }).catch(err => {
            properties['loading'] = true;
            console.log(err);
        });
    } else {
        return {duplicate: true};
    }

    return properties;
}

async function getDealTypeURL($params, $city_id, isCanonical = false) {
    let $url = '';

    if (!isEmpty($params['deal_type'])) {
        if (!is_array($params['deal_type'])) {
            $params['deal_type'] = [$params['deal_type']];
        }

        if (isEmpty($params['types'])) {
            const $defaultTypes = {
                'pre_sale': ['apartment', 'commercial', 'office'],
                'sale': ['apartment', 'house', 'commercial', 'land'],
                'rent': ['apartment', 'house', 'commercial'],
                'mortgage': ['apartment', 'house', 'commercial'],
                'exchange': ['apartment', 'house', 'commercial', 'land', 'villa'],
                'participation': ['apartment', 'commercial', 'residential_complex'],
            };

            let $intersectTypes = [];

            Object.keys($params['deal_type']).forEach(key => {
                const dealType = $params['deal_type'][key];

                if ($intersectTypes.length < 1) {
                    $intersectTypes = $defaultTypes[dealType];
                } else {
                    $intersectTypes = array_intersect($intersectTypes, $defaultTypes[$dealType]);
                }
            });

            $params['types'] = array_unique($intersectTypes);
        } else if (!is_array($params['types'])) {
            $params['types'] = [$params['types']];
        }

        let $types = await getUrlTypesByCityId($city_id);
        let $villaTypes = getVillaTypes();
        let $dealTypes = getUrlDealTypes();
        let $dealTypeValues = getDealTypeValues();

        let $selectedTypes = [];
        let $selectedDealTypes = [];

        Object.keys($params['types']).forEach(key => {
            let $typeURL = '';
            const $type = $params['types'][key];

            if (!isEmpty($type)) {
                const defaultTypeUrl = getUrlTypes($type);

                if (!isEmpty($types[$type])) {
                    $typeURL = $types[$type];
                } else if (defaultTypeUrl) {
                    $typeURL = defaultTypeUrl;
                } else {
                    Object.keys($types).forEach($typeIndex => {
                        const $typesArray = $typeIndex.split('.');

                        if (inArray($type, $typesArray)) {
                            $typeURL = $types[$typeIndex];
                        }
                    });
                }

                let villaType = $params['villa_types'];
                if (is_array(villaType)) {
                    villaType = villaType[0];
                }

                if (!isEmpty(villaType) && !isEmpty($villaTypes[villaType])) {
                    if (!isCanonical) {
                        $typeURL += '-' + $villaTypes[villaType];
                    }
                }

                if (!isEmpty($typeURL)) {
                    $selectedTypes.push($typeURL);
                }
            }
        });

        $selectedTypes = array_unique($selectedTypes);

        Object.keys($params['deal_type']).forEach(key => {
            const $dealType = $params['deal_type'][key];

            if (typeof $params['types'] === "number") {
                $selectedDealTypes.push($dealTypes[$dealTypeValues[$dealType]]);
            } else {
                $selectedDealTypes.push($dealTypes[$dealType]);
            }
        });

        $url += str_replace(' ', '-', $selectedDealTypes.join('-')) +
            '-' + str_replace(' ', '-', $selectedTypes.join('-'));
    }

    delete $params['types'];
    delete $params['deal_type'];
    delete $params['villa_types'];

    return $url;
}

async function getDistrictURL($params, city_id = null, isCanonical = false) {
    let $district_url = '';

    if (!isEmpty($params['district_id'])) {
        let $districtId;

        if (is_array($params['district_id'])) {
            if (isCanonical) {
                if ($params['district_id'].length === 1) {
                    $districtId = $params['district_id'][0];
                }
            } else {
                $districtId = $params['district_id'][0];
            }

            delete $params['district_id'][0];
        } else {
            $districtId = $params['district_id'];
        }

        if (!isEmpty($districtId)) {
            $districtId = typeof $districtId === "object" ? $districtId.value : $districtId;
        }

        if ('work_scope' === $districtId) {
            $district_url += 'محدوده-کاری';
        } else {
            let $district = '';

            if (!isEmpty($districtId)) {
                $district = await getDistrict($districtId);
            }

            if (!isEmpty($district)) {
                $district_url += str_replace(' ', '-', $district.name);
            }
        }

        if (is_array($params['district_id'])) {

            $params['district_id'] = array_unique($params['district_id']);

            if (isEmpty($params['district_id']) || (is_array($params['district_id']) && $params['district_id'].length < 1)) {
                delete $params['district_id'];
            }
        } else {
            delete $params['district_id'];
        }
    }

    return $district_url;
}

function getMoneyURL($params, $type = 'price', $name = 'قیمت', city_id = null, isCannibal = false) {
    const pricesArray = getDefaultPrices();

    let isCustomPrice = false;
    if (isCannibal &&
        (
            (!isEmpty($params['min_price']) && !inArray($params['min_price'], pricesArray))
            ||
            (!isEmpty($params['max_price']) && !inArray($params['max_price'], pricesArray))
        )
    ) {
        isCustomPrice = true;
    }

    if (!isEmpty($params['min_' + $type])) {
        $params['min_' + $type] = str_replace('.', '', $params['min_' + $type]);
    }

    if (!isEmpty($params['max_' + $type])) {
        $params['max_' + $type] = str_replace('.', '', $params['max_' + $type]);
    }

    let $url = '';
    const isEmptyMin = isEmpty($params['min_' + $type]);

    if (!isEmpty($params['min_' + $type])) {
        $params['min_' + $type] = str_replace('.', '', $params['min_' + $type]);
    }

    if (!isEmpty($params['max_' + $type])) {
        $params['max_' + $type] = str_replace('.', '', $params['max_' + $type]);
    }

    let $minValue = '';
    if (!isEmpty($params['min_' + $type]) && $params['min_' + $type] > 0 && city_id !== 259 && !isCustomPrice) {
        $minValue = shortMoneyNumber($params['min_' + $type], '.');
        $url = $minValue['num'] + '-';
    }

    if (city_id !== 259 && !isCustomPrice) {
        delete $params['min_' + $type];
    }

    let $maxValue = '';
    if (!isEmpty($params['max_' + $type]) && $params['max_' + $type] > 0 && (city_id !== 259 || isEmptyMin) && !isCustomPrice) {
        $maxValue = shortMoneyNumber($params['max_' + $type], '.');
        if (!isEmpty($minValue) && $minValue['unit'] !== $maxValue['unit']) {
            $url += $minValue['unit'] + '-';
        }
        $url += 'تا-' + $maxValue['num'] + '-' + $maxValue['unit'];
    } else if (!isEmpty($minValue)) {
        $url += $minValue['unit'] + '-به-بالا';
    }

    if ((city_id !== 259 || isEmptyMin) && !isCustomPrice) {
        delete $params['max_' + $type];
    }

    if (!isEmpty($url)) {
        $url = $name + '-' + $url;
    }

    return $url;
}

function getAreaURL($params, $type = 'infrastructure_area', $name = 'زیربنا', city_id = null, isCannibal = false) {
    const infrastructureAreaArray = getDefaultInfrastructureArea();

    let isCustomArea = false;
    if (isCannibal &&
        (
            (!isEmpty($params['min_infrastructure_area']) && !inArray($params['min_infrastructure_area'], infrastructureAreaArray))
            ||
            (!isEmpty($params['max_infrastructure_area']) && !inArray($params['max_infrastructure_area'], infrastructureAreaArray))
        )
    ) {
        isCustomArea = true
    }


    let $url = '';
    const isEmptyMin = isEmpty($params['min_' + $type]);

    if (!isEmpty($params['min_' + $type])) {
        $params['min_' + $type] = str_replace('.', '', $params['min_' + $type]);
    }

    if (!isEmpty($params['max_' + $type])) {
        $params['max_' + $type] = str_replace('.', '', $params['max_' + $type]);
    }

    let $minValue = '';
    if (!isEmpty($params['min_' + $type]) && $params['min_' + $type] > 0 && city_id !== 259 && !isCustomArea) {
        $minValue = shortAreaNumber($params['min_' + $type], '.');
        $url = $minValue['num'] + '-';
    }

    if (city_id !== 259 && !isCustomArea) {
        delete $params['min_' + $type];
    }

    let $maxValue = '';
    if (!isEmpty($params['max_' + $type]) && $params['max_' + $type] > 0 && (city_id !== 259 || isEmptyMin) && !isCustomArea) {
        $maxValue = shortAreaNumber($params['max_' + $type], '.');
        if (!isEmpty($minValue) && $minValue['unit'] !== $maxValue['unit']) {
            $url += $minValue['unit'] + '-';
        }
        $url += 'تا-' + $maxValue['num'] + '-' + $maxValue['unit'];
    } else if (!isEmpty($minValue)) {
        $url += $minValue['unit'] + '-به-بالا';
    }

    if ((city_id !== 259 || isEmptyMin) && !isCustomArea) {
        delete $params['max_' + $type];
    }

    if (!isEmpty($url)) {
        $url = $name + '-' + $url;
    }

    return $url;
}

/**
 * @param items {Object}
 * @param $getParams {Boolean}
 * @param isCanonical {Boolean}
 *
 * @return Object
 * */
async function getSearchLink(items, $getParams = true, isCanonical = false) {
    let $params = JSON.parse(JSON.stringify(items)); // deep copy
    const city_id = !isEmpty($params['city_id']) ? $params['city_id'] : getDefaultCity();
    let hasLoan = false;
    let hasLoanAndCanonical;
    let $url;
    let isCannibal = false;


    const unsetParams = ['selected_range', 'checkRedirectToLanding', 'properties', 'work_scope',
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    ];

    for (let unUse of unsetParams) {
        if (!isEmpty($params[unUse])) {
            delete $params[unUse];
        }
    }

    if (!isEmpty($params['district_id']) && is_array($params['district_id']) && $params['district_id'].length < 1) {
        delete $params['district_id'];
    }

    if (!isEmpty($params['loan']) && $params['loan'] === false) {
        delete $params['loan'];
    } else if (!isEmpty($params['loan']) && $params['loan']) {
        hasLoan = true;
    }

    if (!isEmpty($params['has_media']) && $params['has_media'] === false) {
        delete $params['has_media'];
    }

    if (!isEmpty($params['has_video']) && $params['has_video'] === false) {
        delete $params['has_video'];
    }

    let hasPool = false;
    if (!isEmpty($params['has_pool']) && $params['has_pool'] === false) {
        delete $params['has_pool'];
    }

    if (ObjectLength($params) === 4 && !isEmpty($params['city_id']) && !isEmpty($params['deal_type']) && !isEmpty($params['types']) && $params['types'].length === 1 && $params['has_pool'] == 1) {
        hasPool = true;
    }

    hasLoanAndCanonical = (hasLoan && isCanonical);

    if (city_id === 259
        && !isEmpty($params['types']) && $params['types'].length === 1 && $params['types'][0] === 'apartment'
        && !isEmpty($params['deal_type']) && $params['deal_type'] === 'sale'
    ) {
        isCannibal = true;
    }

    if (!isEmpty($params['district_id']) && !is_array($params['district_id']) && typeof $params['district_id'] === "number") {
        $params['district_id'] = [$params['district_id']];
        $params['district_name'] = [$params['district_name']];
    }

    if (!isEmpty($params['city_id']) && !isEmpty($params['district_id']) && $params['district_id'].length === 1 && $params['city_id'] === 207 && $params['district_id'][0] === 199) {
        $params['city_id'] = 262;
        $params['city_name'] = 'گلبهار';
        delete $params['district_id'];
    }

    if (!isEmpty($params['city_id']) && !isEmpty($params['district_id']) && $params['district_id'].length === 1 && $params['city_id'] === 259 && $params['district_id'][0] === 9362) {
        $params['city_id'] = 3573;
        $params['city_name'] = 'پرند';
        delete $params['district_id'];
    }

    if (!isEmpty($params['city_id']) && !isEmpty($params['district_id']) && $params['district_id'].length === 1 && $params['city_id'] === 259 && $params['district_id'][0] === 9273) {
        $params['city_id'] = 3165;
        $params['city_name'] = 'پردیس';
        delete $params['district_id'];
    }

    if (!isEmpty($params['city_id']) && ObjectLength($params) === 1) {
        $url = '/' + await getCityURL(!isEmpty($params['city_name']) ? $params['city_name'] : city_id, true);
        delete $params['city_id'];
    } else if (!isCanonical && (isEmpty($params['city_id']) || isEmpty($params['deal_type']) || isEmpty($params['types']) || $params['types'].length < 1)) {
        // $url = (is_panel() ? '/panel' : '') + '/properties';
        $url = '/properties';
    } else {
        $url = '/' + await getCityURL(!isEmpty($params['city_name']) ? $params['city_name'] : city_id, true);
        delete $params['city_id'];

        Object.keys($params).forEach(key => {
            const $param = $params[key];

            if (('min_age' === key || 'max_age' === key) && $param === 0) {
                if (isEmpty($param)) {
                    delete $params[key];
                }
            }
        });

        let $dealTypeGroup = '';
        if (!isEmpty($params['deal_type'])) {
            if ($params['deal_type'] === 'rent') {
                delete $params['min_price'];
                delete $params['max_price'];

                $dealTypeGroup = 'rent';
            }

            if ($params['deal_type'] === 'mortgage') {
                delete $params['min_price'];
                delete $params['max_price'];

                if (isEmpty($dealTypeGroup)) {
                    delete $params['min_rent'];
                    delete $params['max_rent'];
                }

                $dealTypeGroup = 'mortgage';
            } else if (isEmpty($dealTypeGroup)) {
                $dealTypeGroup = 'sale';
                delete $params['min_rent'];
                delete $params['max_rent'];
                delete $params['min_mortgage'];
                delete $params['max_mortgage'];
            }
        }


        const dealTypeUrl = await getDealTypeURL($params, city_id, isCanonical);
        if (!isEmpty(dealTypeUrl)) {
            $url += '/' + encodeURI(dealTypeUrl);
        }

        const hasDistrict = (!isEmpty($params['district_id']) && ($params['district_id'].length > 0));

        if (!hasLoanAndCanonical) {
            const district_url = await getDistrictURL($params, city_id, isCanonical);
            if (!isEmpty(district_url)) {
                $url += '/' + encodeURI(district_url);
            }

            if (!hasDistrict || !isCannibal) {
                const priceUrl = getMoneyURL($params, 'price', 'قیمت', (isCannibal ? city_id : null), isCannibal);
                if (!isEmpty(priceUrl)) {
                    $url += '/' + encodeURI(priceUrl);
                } else {
                    const mortgageUrl = getMoneyURL($params, 'mortgage', 'رهن');
                    if (!isEmpty(mortgageUrl)) {
                        $url += '/' + encodeURI(mortgageUrl);
                    }

                    const rentUrl = getMoneyURL($params, 'rent', 'اجاره');
                    if (!isEmpty(rentUrl)) {
                        $url += '/' + encodeURI(rentUrl);
                    }
                }

                const infrastructureUrl = getAreaURL($params, 'infrastructure_area', 'زیربنا', (isCannibal ? city_id : null), isCannibal);
                if (!isEmpty(infrastructureUrl)) {
                    $url += '/' + encodeURI(infrastructureUrl);
                }

                const landUrl = getAreaURL($params, 'land_area', 'زمین');
                if (!isEmpty(landUrl)) {
                    $url += '/' + encodeURI(landUrl);
                }
            }

            if (hasPool && (!isCanonical || (isCanonical && city_id === 3198))) { // 3198 => shomal Id
                $url += '/' + encodeURI('استخردار');
                delete $params['has_pool'];
            }

        }


        if (!isEmpty($params['loan']) || !isEmpty($params['has_loan'])) {
            $url += '/' + encodeURI('با-وام');

            delete $params['loan'];
            delete $params['has_loan'];
        }
    }

    if ((!isEmpty($params['map']) && $params['map']) || !isEmpty($params['bound']) || !isEmpty($params['center'])) {
        $url += '/' + encodeURI('نقشه');

        delete $params['map'];
    }

    if (!isEmpty($params['page']) && $params['page'] === 0) {
        delete $params['page'];
    }

    if ($getParams && ObjectLength($params) > 0) {
        $url += '?' + httpBuildQuery($params);
    }

    const tmpUrl = $url.split('?');
    if (isEmpty(tmpUrl[1])) {
        $url = tmpUrl[0];
    }
// console.log(decodeURI($url));
    return {
        url: $url,
        params: $params
    }
}

/**
 * @param filters {Object}
 * @param isBadeSaba {Boolean}
 * */
async function checkFiltersForRedirect(filters, isBadeSaba = false) {
    let $params = JSON.parse(JSON.stringify(filters)); // deep copy

    const city_id = !isEmpty($params['city_id']) ? Number($params['city_id']) : '';
    const deal_type = !isEmpty($params['deal_type']) ? $params['deal_type'] : '';
    const types = !isEmpty($params['types']) ? $params['types'] : [];
    const districtIds = !isEmpty($params['district_id']) ? $params['district_id'] : [];

    const unsetParams = ['city_id', 'deal_type', 'types', 'badesaba', 'work_scope',
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    ];

    for (let unUse of unsetParams) {
        if (!isEmpty($params[unUse])) {
            delete $params[unUse];
        }
    }

    //delete $params['sort'];

    if (!isEmpty($params['loan']) && $params['loan'] === false) {
        delete $params['loan'];
    }

    if (!isEmpty($params['has_media']) && $params['has_media'] === false) {
        delete $params['has_media'];
    }

    if (!isEmpty($params['has_video']) && $params['has_video'] === false) {
        delete $params['has_video'];
    }

    if (!isEmpty($params['has_pool']) && $params['has_pool'] === false) {
        delete $params['has_pool'];
    }

    let result = {
        redirect: false,
        href: null,
        hrefAs: null,
        landing: null,
    };

    if (!isBadeSaba && !isEmpty(city_id) && !isEmpty(deal_type) && types.length === 1 && ObjectLength($params) < 1) {
        const type = types[0];
        const checkRedirectToNorthCity = checkRedirectToNorthCityLanding(city_id, deal_type, type);

        if (checkRedirectToNorthCity.redirect) {
            result = checkRedirectToNorthCity;
        } else if (city_id === 3198 && deal_type === 'sale' && type === 'villa.house') {
            result = {
                redirect: true,
                href: '/properties/landing/shomal',
                hrefAs: '/املاک-شمال/خرید-فروش-ویلا',
                landing: 'shomal',
                type: 'property'
            }
        } else if (city_id === 207 && deal_type === 'sale' && type === 'apartment') {
            result = {
                redirect: true,
                href: '/properties/landing/mashhad',
                hrefAs: '/املاک-مشهد/خرید-فروش-آپارتمان',
                landing: 'mashhad',
                type: 'property'
            }
        } else if (city_id === 259 && deal_type === 'sale' && type === 'apartment') {
            result = {
                redirect: true,
                href: '/properties/landing/tehran',
                hrefAs: '/املاک-تهران/خرید-فروش-آپارتمان',
                landing: 'tehran',
                type: 'property'
            }
        }
    } else if (ObjectLength($params) < 1 && isEmpty(deal_type) && types.length === 0 && !isEmpty(city_id)) {
        if (city_id === 259) {
            result = {
                redirect: true,
                href: '/home/landing/tehran',
                hrefAs: '/املاک-تهران' + (isBadeSaba ? '?badesaba=true' : ''),
                landing: 'tehran_home',
                type: 'home'
            }
        } else if (city_id === 207) {
            result = {
                redirect: true,
                href: '/home/landing/mashhad',
                hrefAs: '/املاک-مشهد' + (isBadeSaba ? '?badesaba=true' : ''),
                landing: 'mashhad_home',
                type: 'home'
            }
        } else if (city_id === 3165) {
            result = {
                redirect: true,
                href: '/home/landing/pardis',
                hrefAs: '/املاک-پردیس' + (isBadeSaba ? '?badesaba=true' : ''),
                landing: 'pardis_home',
                type: 'home'
            }
        } else {
            const city = getCity(city_id);
            const city_url = '/املاک-' + (!isEmpty(city) ? str_replace(' ', '-', city.name) : '');
            const checkRedirected = await checkUrlParams(city_url, linkTypes.SEARCH);


            if (isEmpty(checkRedirected) || !checkRedirected.redirect) {
                result = {
                    redirect: true,
                    href: '/home/landing/cities',
                    hrefAs: city_url,
                    landing: 'cities_home',
                    type: 'home'
                }
            }
        }
    } else if (!isBadeSaba && !isEmpty(city_id) && !isEmpty(deal_type) && types.length === 1 && districtIds.length === 1) {
        delete $params['district_id'];
        let districtId = null;//(!isEmpty(districtIds[0]) && typeof districtIds[0] === "number") ? districtIds[0]

        if (!isEmpty(districtIds[0])) {
            districtId = typeof districtIds[0] === "object" ? districtIds[0].value : districtIds[0];
        }

        if (districtId === 2759 && ObjectLength($params) < 1) { // نزدیک حرم امام رضا
            result = {
                redirect: true,
                href: '/pages/properties/landing/holy_shrine',
                hrefAs: '/املاک-مشهد/خرید-فروش-آپارتمان/نزدیک-حرم-امام-رضا',
                landing: 'holyShrine',
                district_id: 2759,
            }
        }
    }

    return result;
}

/**
 * @param city_id {number}
 *
 * @return Promise
 * */
function getSabaEstimateByCity(city_id) {
    const path = '/search/v1/properties/saba/byCity/' + city_id;

    return new Promise((resolve, reject) => {
        axios.get(path).then(result => {
            resolve(result.data);
        }).catch(err => {
            reject(err);
        })
    })
}

export {
    getProperties,
    getSearchLink,
    checkFiltersForRedirect,
    getSabaEstimateByCity
}